import React, { FC, useId } from 'react';
import styled, { css } from 'styled-components';

import A11yHidden from '@rover/kibble/styles/A11yHidden';
import MQ from '@rover/kibble/styles/MediaQuery';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';

import type { ButtonGroupVariant } from './ButtonGroup';

const Checkbox = styled.input`
  ${A11yHidden}
  height: 100%;
  width: 100%;
`;

type LabelProps = {
  active: boolean;
  bigLabel?: boolean;
  variant?: ButtonGroupVariant;
};

const Label = styled.label<LabelProps>`
  align-items: center;
  position: relative;
  background: ${({ theme }) => theme.background.primary};
  border-radius: ${({ theme }) => theme.radii.secondary};
  box-shadow: ${({ theme }) =>
    `inset 0 0 0 ${theme.borderWidth.primary} ${theme.colors.interactive_border.primary_base}`};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  display: flex;
  font-weight: normal;
  justify-content: center;
  margin: 0 0 0 -1px;
  min-height: 40px;
  ${(props) =>
    props.variant === 'rounded'
      ? css`
          width: 42px;
          height: 42px;
          flex-shrink: 0;
        `
      : css`
          width: 100%;
        `}
  font-size: ${({ bigLabel, theme }) =>
    bigLabel ? theme.text['200'].fontSize : theme.text['100'].fontSize};

  &:has(:focus-visible) {
    outline: 2px solid ${({ theme }) => theme.colors.interactive_border.primary_active};
    outline-offset: 3px;
  }

  ${(props) =>
    props.variant !== 'rounded' &&
    css`
      &:first-child {
        margin-left: 0;

        &:not(:last-child) {
          border-bottom-right-radius: 0;
          border-top-right-radius: 0;
        }
      }

      &:not(:first-child) {
        &:last-child {
          border-bottom-left-radius: 0;
          border-top-left-radius: 0;
        }
        &:not(:last-child) {
          border-radius: 0;
        }
      }
    `}

  &:hover {
    background: ${({ theme }) => theme.background.interactive.primary_pressed};
    text-decoration: none;
  }

  ${(props) =>
    !props.active
      ? ''
      : css`
          background-color: ${({ theme }) =>
            props.variant === 'edit'
              ? theme.background.accent_secondary
              : theme.background.primary} !important;
          box-shadow: ${({ theme }) =>
            `inset 0 0 0 2px ${
              props.variant === 'edit'
                ? theme.colors.interactive_text.primary_active
                : theme.colors.interactive_border.primary_active
            }`};
          color: ${({ theme }) => theme.colors.text.primary};
          z-index: 3;

          &:hover {
            box-shadow: ${({ theme }) =>
              `inset 0 0 0 2px ${
                props.variant === 'edit'
                  ? theme.colors.interactive_text.primary_active
                  : theme.colors.interactive_border.primary_active_pressed
              }`};
          }
        `};

  ${MQ.XS.toString()} {
    font-size: ${({ theme }) => theme.text['100'].fontSize};
  }
`;

export type Props = {
  buttonType: 'radio' | 'checkbox';
  checked?: boolean;
  label: string;
  name?: string;
  onToggle: (...args: any[]) => any;
  value: string | boolean;
  bigLabel?: boolean;
  borderRadius?: string;
  variant?: ButtonGroupVariant;
  disabled?: boolean;
};

const ButtonGroupButton: FC<Props> = ({
  buttonType,
  checked,
  label,
  name,
  onToggle,
  value,
  bigLabel,
  borderRadius,
  disabled,
  ...other
}) => {
  const id = useId();
  const isMobile = useMatchMedia(MQ.MOBILE.getBreakpointString());

  return (
    <Label
      {...other}
      style={{ borderRadius, ...(disabled && { cursor: 'not-allowed' }) }}
      active={buttonType === 'checkbox' ? Boolean(value) : !!checked}
      htmlFor={id}
      bigLabel={bigLabel}
      aria-disabled={disabled || undefined}
    >
      <Checkbox
        aria-label={other['aria-label'] ?? ((isMobile && label) || undefined)}
        type={buttonType}
        role={buttonType === 'radio' ? 'radio' : 'checkbox'}
        checked={checked}
        id={id}
        name={name}
        onChange={onToggle}
        value={String(value)}
        data-testid="styled-input"
        disabled={disabled}
      />
      <span aria-hidden={other['aria-label'] || isMobile ? 'true' : undefined}>{label}</span>
    </Label>
  );
};

export default ButtonGroupButton;
